// src/components/Header/HeaderBar.js
import React, { useEffect, useMemo, useState } from "react";
import { Layout, Menu, Space, Button, Typography, Avatar, Drawer } from "antd";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { MenuOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useAuth } from "../../context/AuthContext";

const { Header } = Layout;

export default function HeaderBar({ themeName, isDarkTheme }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthed, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [nowText, setNowText] = useState(() =>
    dayjs().format("YYYY-MM-DD HH:mm:ss")
  );

  useEffect(() => {
    const timer = setInterval(() => {
      setNowText(dayjs().format("YYYY-MM-DD HH:mm:ss"));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const selectedKey = useMemo(() => {
    const p = location.pathname;
    if (p.startsWith("/post/")) return "/home";
    if (p.startsWith("/archive")) return "/archive";
    if (p.startsWith("/me")) return "/me";
    if (p.startsWith("/write")) return "/write";
    if (p.startsWith("/about")) return "/about";
    if (p.startsWith("/settings")) return "/settings";
    if (p.startsWith("/home")) return "/home";
    return "/home";
  }, [location.pathname]);

  const items = [
    { key: "/home", label: "首页" },
    { key: "/archive", label: "归档" },
    { key: "/about", label: "关于" },
    { key: "/me", label: "我的" },
    { key: "/settings", label: "设置" },
  ];

  if (isAuthed) {
    items.splice(2, 0, { key: "/write", label: "写文章" });
  }

  const menuTheme = isDarkTheme ? "dark" : "light";
  const desktopItems = items.map((item) => ({
    key: item.key,
    label: <NavLink to={item.key}>{item.label}</NavLink>,
  }));
  const drawerItems = items.map((item) => ({
    key: item.key,
    label: item.label,
  }));
  const handleDrawerClick = (e) => {
    navigate(e.key);
    setMobileOpen(false);
  };

  return (
    <Header
      style={{
        position: "sticky",
        top: 0,
        zIndex: 10,
        padding: 0,
        background: "var(--header-bg)",
        borderBottom: "1px solid var(--header-border)",
        backdropFilter: "blur(10px)",
      }}
    >
      <div
        className="app-header-inner"
        style={{
          width: "100%",
          maxWidth: 1100,
          margin: "0 auto",
          padding: "0 16px",
          boxSizing: "border-box",
          display: "flex",
          alignItems: "center",
          gap: 16,
        }}
      >
        <Space align="center" style={{ minWidth: 180 }}>
          <Avatar
            size={28}
            src={user?.avatar || user?.avatarUrl || undefined}
            style={{ background: "var(--accent-gradient)" }}
          >
            {user?.username?.slice(0, 1)?.toUpperCase() || "B"}
          </Avatar>
          <Typography.Text
            strong
            style={{ color: "var(--header-text)", fontSize: 16 }}
          >
            {user?.username ? `${user.username} 的博客` : "我的博客"}
          </Typography.Text>
        </Space>

        <div className="app-nav" style={{ flex: 1 }}>
          <Menu
            theme={menuTheme}
            mode="horizontal"
            selectedKeys={[selectedKey]}
            items={desktopItems}
            style={{ background: "transparent" }}
          />
        </div>

        {/* 右侧按钮：未登录就啥也不显示 */}
        <Space size={12} className="app-header-actions">
          <Button
            className="app-menu-button"
            type="text"
            icon={<MenuOutlined />}
            onClick={() => setMobileOpen(true)}
            aria-label="打开菜单"
          />
          <Typography.Text
            className="app-clock"
            style={{
              padding: "6px 12px",
              borderRadius: 999,
              color: "var(--clock-text)",
              background: "var(--clock-bg)",
              border: "1px solid var(--clock-border)",
              boxShadow: "var(--clock-shadow)",
              backdropFilter: "blur(8px)",
            }}
          >
            {nowText}
          </Typography.Text>
          {isAuthed ? (
            <Button
              onClick={() => {
                logout();
                navigate("/auth", { replace: true });
              }}
            >
              退出
            </Button>
          ) : null}
        </Space>
      </div>
      <Drawer
        title="菜单"
        placement="right"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        bodyStyle={{ padding: 0 }}
      >
        <Menu
          mode="inline"
          selectedKeys={[selectedKey]}
          items={drawerItems}
          onClick={handleDrawerClick}
        />
      </Drawer>
    </Header>
  );
}
